import { useEffect, useRef } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { ContentEditor, type Json } from './ContentEditor';
import { fieldLabels, humanize } from '../adminLabels';
import { plural } from '../../core/format/text';
type Props = {
  value: Json;
  onChange: (value: Json) => void;
  /** Blok dat open moet (bijv. na een zoekresultaat). */
  focusKey: string | null;
  /** Telt op bij elke zoekkeuze, zodat hetzelfde blok opnieuw kiezen weer opent en scrollt. */
  focusTick?: number;
};
const isObject = (v: Json): v is {
  [key: string]: Json;
} => typeof v === 'object' && v !== null && !Array.isArray(v);

/** De kop van een blok zoals hij op de site staat, als herkenning in de dichte balk. */
export function previewOf(v: Json): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.length ? plural(v.length, 'item', 'items') : 'Leeg';
  if (isObject(v)) {
    const heading = v.heading;
    if (isObject(heading) && typeof heading.text === 'string' && heading.text) return heading.text;
    for (const key of ['title', 'name', 'eyebrow', 'text', 'body']) {
      const x = v[key];
      if (typeof x === 'string' && x) return x;
    }
  }
  return '';
}

/**
 * Een pagina als rij dichtgeklapte blokken, in de volgorde van de site. Eén lang formulier
 * van tachtig velden is niet te overzien; zo zie je eerst de opbouw en open je alleen het
 * blok dat je wilt aanpassen. Losse tekstregels bovenaan de pagina blijven gewoon open.
 */
export function ContentSections({
  value,
  onChange,
  focusKey,
  focusTick
}: Props) {
  const refs = useRef<Record<string, HTMLDetailsElement | null>>({});
  useEffect(() => {
    if (!focusKey) return;
    const el = refs.current[focusKey];
    if (!el) return;
    el.open = true;
    el.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }, [focusKey, focusTick]);

  // Klantcitaten e.d. zijn één lijst: die heeft geen blokken.
  if (!isObject(value)) return <ContentEditor value={value} onChange={onChange} data-id="element-4968" data-mp="yb5utju8l0i7" />;
  const entries = Object.entries(value).filter(([k]) => !fieldLabels[k]?.hidden);
  return <div className="space-y-3" data-id="element-4969" data-mp="139zbfl4tl7ml">
      {entries.map(([key, v], i) => {
      const set = (next: Json) => onChange({
        ...value,
        [key]: next
      });
      if (typeof v !== 'object' || v === null) {
        return <div key={key} className="rounded-md border border-line bg-white p-4" data-id="element-4970" data-mp="1c816zl5sb82h">
              <ContentEditor fieldKey={key} value={v} onChange={set} data-id="element-4971" data-mp="1h98h3qvegjcb" />
            </div>;
      }
      const preview = previewOf(v);
      return <details key={key} ref={el => {
        refs.current[key] = el;
      }} open={i === 0} className="group scroll-mt-24 rounded-md border border-line bg-white open:shadow-sm" data-id="element-4972" data-mp="1r0csyhps3o2x" data-mpi={i}>
            <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3.5 [&::-webkit-details-marker]:hidden" data-id="element-4973" data-mp="1zy5brvupldv2">
              <span className="min-w-0 flex-1" data-id="element-4974" data-mp="2f39pnuuu4c2l">
                <span className="block text-[14px] font-medium text-ink" data-id="element-4975" data-mp="2f0wx6nrgajba">{humanize(key)}</span>
                {preview && <span className="mt-0.5 block truncate text-[12px] text-muted" data-id="element-4976" data-mp="2a5zojhy32byx">{preview}</span>}
              </span>
              <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft group-open:rotate-180" strokeWidth={1.5} aria-hidden="true" data-id="element-4977" data-mp="2138enmkuvo9e" />
            </summary>
            <div className="border-t border-line p-4" data-id="element-4978" data-mp="2suxzfrhm2oge">
              {fieldLabels[key]?.hint && <p className="mb-4 text-[12px] text-muted" data-id="element-4979" data-mp="2o2d94zxwythm">{fieldLabels[key]?.hint}</p>}
              <ContentEditor fieldKey={key} value={v} onChange={set} depth={0} data-id="element-4980" data-mp="2k2w1bzv0lk9a" />
            </div>
          </details>;
    })}
    </div>;
}